import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable, tap } from 'rxjs';
import { ReviewPeriodStatusEnum } from 'src/app/enums/review-period-status.enum';
import { Employee } from 'src/app/models/employee.model';
import { Report } from 'src/app/models/report.model';
import { PersonLegend } from 'src/app/models/report/person-legend.model';
import { ReportOptions } from 'src/app/models/report/report-options.model';
import { State } from '../../models/state.model';
import { ReportService, StateService } from '../../services';
import { TextGuideService } from 'src/app/services/text-guide.service';
import { QuestionSetForReport } from 'src/app/models/question-module-report.model';
import { ViewHelper } from 'src/app/shared/helpers';
import { ReviewPeriod } from 'src/app/models/review-period.model';
import { ReviewTypeEnum } from 'src/app/enums/review-type.enum';
import { Temporal } from '@js-temporal/polyfill';
import { QuestionComments } from 'src/app/models/report/question-comments.model';
import { ReportType } from 'src/app/enums/report-type.enum';
import { EmployeeStatus } from 'src/app/enums/employee-status.enum';
import { EmployeeHelper } from 'src/app/helpers/employee.helper';

@UntilDestroy()
@Component({
    selector: 'report',
    templateUrl: './report.component.html',
    styleUrls: ['./report.component.scss']
})
export class ReportComponent implements OnInit {
        
    public state: State;
    public data$: Observable<Report>;
    public legend: PersonLegend[] = [];
    public reportOptions: ReportOptions;
    public isError = false;
    public errorMessage: string;
    
    public questionComments: QuestionComments[] = [];

    constructor(
        private reportService: ReportService,
        stateService: StateService,
        private route: ActivatedRoute,
        private guideService: TextGuideService,
        private translate: TranslateService) {
        this.state = stateService.state;
    }

    ngOnInit() {

        this.route.params
        .pipe(untilDestroyed(this))
        .subscribe(params => {
            if (params['employeeId'] && params['reviewPeriodId'])
            {
                const guide = this.guideService.getGuide('assessment:TER');

                this.translate.stream([guide.titleKey, guide.subtitleKey])
                    .pipe(untilDestroyed(this))
                    .subscribe(translations => {
                    this.reportOptions = {
                        title: translations[guide.titleKey],
                        subtitle: translations[guide.subtitleKey],
                        titleIndexText: guide.titleIndexText,
                        logoLink: guide.logoLink,
                        reportType: ReportType.PanoramaAura
                    } as ReportOptions;
                    });

                this.data$ = this.reportService.getReport(params['employeeId'], params['reviewPeriodId']).pipe(
                    tap(data => {
                      
                        // this.isError = true;
                        // if (err?.error?.errorData?.key)
                        // {
                        //     const msg = this.msgService.getErrorMessage(err.error.errorData.key);
                        //     this.errorMessage = msg.text;
                        // }

                        this.calculateIndex(data.questionSets);

                        const employee = {
                            id: params['employeeId'],
                            name: data.name + this.getInactiveSuffix(data.employeeStatus),
                            email: data.email
                        } as Employee;
                        
                        // legend                       
                        this.legend.push({ person: employee });

                        this.reportOptions = {
                            ...this.reportOptions, 
                            dateFrom: this.getDateFrom(data.reviewPeriod),
                            dateTo: this.getDateTo(data.reviewPeriod),
                            gaugeOptions: {
                                gaugeType: 'full',
                                appendText: '%',
                                size: 82,
                                value: data.totalWeightedAverage * 100,
                                label: ''
                            }
                        } as ReportOptions;

                    })
                );

            }

            ViewHelper.scrollToTop();
        })
    }

    getInactiveSuffix(employeeStatus: EmployeeStatus): string {
        if (employeeStatus === EmployeeStatus.Inactive)
            return ' [' + this.translate.instant('EMPLOYEE.INACTIVE').toLocaleLowerCase() + ']';
        return '';
    }
    
    getDateFrom(rp: ReviewPeriod): Temporal.PlainDate | null {
        if (rp.reviewType === ReviewTypeEnum.TER)
            return rp.panoramaAuraStartDate ? Temporal.PlainDate.from(rp.panoramaAuraStartDate) : null;
        else
            return rp.startDate ? Temporal.PlainDate.from(rp.startDate) : null;
    }

    getDateTo(rp: ReviewPeriod): Temporal.PlainDate | null {
        if (rp.reviewType === ReviewTypeEnum.TER)
            return rp.panoramaAuraEndDate ? Temporal.PlainDate.from(rp.panoramaAuraEndDate) : null;
        else
            return rp.endDate ? Temporal.PlainDate.from(rp.endDate) : null;
    }

    calculateIndex(qSets: QuestionSetForReport[]) {
        let index = 1;
        qSets.forEach(qSet => {
            qSet.childQuestionSets.forEach(module => {
                module.childQuestionSets.forEach(group => {
                    group.questions.forEach(question => {
                        question.index = index++;

                        if (question.comments && question.comments.length > 0)
                        {
                            this.questionComments.push({
                                questionName: question.name,
                                questionIndex: question.index,
                                comments: question.comments
                            } as QuestionComments);
                        }
                    });
                });
            });
        });
    }  

    hasItems(items: any[]) {
        return (items && items.length > 0) || this.questionComments.length > 0;
    }

    getColumnCount(leadersCount: number): string {
        let count = 3;
        
        if (this.state.config.c.reports.separatePeerAndSubordinate)
            count += 1;
        
        count += leadersCount != 0 ? leadersCount : 1;

        return count.toString();
    }

    getIndex(groupIndex: number, groupLength: number, questionIndex: number): number {
        console.log('groupIndex')
        return (groupIndex * groupLength) + questionIndex + 1;
    }

    isRpClosed(reviewPeriod: ReviewPeriod): boolean {
        return reviewPeriod.status === ReviewPeriodStatusEnum.Closed;
    }
}
